import React, { useRef, useState, useEffect } from 'react';
import { initI18n, useTranslation } from '../../shared/i18n/index';
import { assetPath } from '../../shared/assetPath';
import { LanguageSwitcher } from '../../shared/components/LanguageSwitcher';

// Header Component
const Header = ({ loginPath, t }) => (
  <header
    className="position-fixed w-100"
    style={{
      top: 0,
      left: 0,
      zIndex: 1000,
      background: 'white',
      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      borderBottom: '1px solid #f3f4f6',
    }}
  >
    <nav className="container">
      <div className="d-flex justify-content-between align-items-center py-3">
        {/* Logo */}
        <a href="/" className="d-flex align-items-center text-decoration-none">
          <img
            src={assetPath('powerkid-icon.png')}
            alt="PowerKid Logo"
            className="me-2"
            style={{ width: '40px', height: '40px' }}
          />
          <span className="fw-bold text-dark" style={{ fontSize: '24px' }}>
            PowerKid
          </span>
        </a>

        {/* Navigation buttons */}
        <div className="d-flex align-items-center gap-2">
          <LanguageSwitcher variant="light" />
          <a
            href={loginPath}
            className="btn btn-link text-dark text-decoration-none fw-medium"
          >
            {t('landing.nav.login')}
          </a>
          <a
            href={loginPath}
            className="btn fw-medium text-white"
            style={{
              background: 'linear-gradient(to right, #667eea, #764ba2)',
              borderRadius: '8px',
            }}
          >
            {t('landing.nav.getStarted')}
          </a>
        </div>
      </div>
    </nav>
  </header>
);

// Hero Section Component
const HeroSection = ({ loginPath, t }) => (
  <section
    className="position-relative overflow-hidden"
    style={{
      paddingTop: '77px',
      background: 'linear-gradient(22.62deg, #667eea 50%, #764ba2 120.71%)',
      minHeight: '600px',
    }}
  >
    <div className="container py-5">
      <div className="row align-items-center">
        <div className="col-lg-6 text-white">
          <h1 className="fw-bold mb-4" style={{ fontSize: '60px', lineHeight: '60px' }}>
            {t('landing.hero.title')}{' '}
            <span style={{ color: '#f093fb' }}>{t('landing.hero.titleHighlight')}</span>
          </h1>
          <p
            className="mb-4"
            style={{ fontSize: '20px', lineHeight: '33px', color: '#f3f4f6' }}
          >
            {t('landing.hero.description')}
          </p>
          <a
            href={loginPath}
            className="btn btn-light btn-lg fw-semibold px-4 d-inline-flex align-items-center gap-2"
            style={{ borderRadius: '8px', color: '#667eea' }}
          >
            {t('landing.hero.startFree')}
            <span
              className="badge text-white fw-semibold"
              style={{
                backgroundColor: '#22c55e',
                borderRadius: '9999px',
                fontSize: '12px',
                padding: '4px 10px',
              }}
            >
              {t('landing.hero.freeBadge')}
            </span>
          </a>
        </div>
        <div className="col-lg-6 mt-4 mt-lg-0">
          <div
            className="overflow-hidden"
            style={{
              borderRadius: '16px',
              boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
              height: '384px',
            }}
          >
            <picture>
              <source srcSet={assetPath('hero-image.webp')} type="image/webp" />
              <img
                src={assetPath('hero-image.png')}
                alt={t('landing.hero.heroImageAlt')}
                className="w-100 h-100"
                style={{ objectFit: 'cover' }}
              />
            </picture>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Benefit Card Component
const BenefitCard = ({ icon, iconBg, title, description }) => (
  <div
    className="bg-white rounded-3 p-4 mb-3"
    style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
  >
    <div className="d-flex">
      <div
        className="rounded-2 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
        style={{ width: '48px', height: '48px', backgroundColor: iconBg }}
      >
        <i className={`bi ${icon}`} style={{ fontSize: '20px', color: '#333' }}></i>
      </div>
      <div>
        <h4 className="fw-bold mb-2" style={{ fontSize: '20px', color: '#111827' }}>
          {title}
        </h4>
        <p className="mb-0" style={{ fontSize: '16px', color: '#4b5563', lineHeight: '24px' }}>
          {description}
        </p>
      </div>
    </div>
  </div>
);

// Benefits Section Component
const BenefitsSection = ({ t }) => (
  <section
    className="py-5"
    style={{
      background: 'linear-gradient(142.52deg, #faf5ff 0%, #eff6ff 70.71%)',
    }}
  >
    <div className="container py-4">
      {/* Section Header */}
      <div className="text-center mb-5">
        <h2
          className="fw-bold mb-3"
          style={{ fontSize: '36px', color: '#111827', lineHeight: '40px' }}
        >
          {t('landing.benefits.title')}
        </h2>
        <p style={{ fontSize: '20px', color: '#4b5563', lineHeight: '28px' }}>
          {t('landing.benefits.subtitle')}
        </p>
      </div>

      <div className="row g-4">
        {/* Left Column - Benefit Cards */}
        <div className="col-lg-6">
          <BenefitCard
            icon="bi-book"
            iconBg="#dcfce7"
            title={t('landing.benefits.autonomousLearning.title')}
            description={t('landing.benefits.autonomousLearning.description')}
          />
          <BenefitCard
            icon="bi-clock"
            iconBg="#dbeafe"
            title={t('landing.benefits.getTimeBack.title')}
            description={t('landing.benefits.getTimeBack.description')}
          />
          <BenefitCard
            icon="bi-shield-check"
            iconBg="#f3e8ff"
            title={t('landing.benefits.safeEnvironment.title')}
            description={t('landing.benefits.safeEnvironment.description')}
          />
        </div>

        {/* Right Column - Testimonial Card */}
        <div className="col-lg-6">
          <div
            className="bg-white h-100 p-4"
            style={{
              borderRadius: '16px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.1), 0 10px 15px rgba(0,0,0,0.1)',
            }}
          >
            {/* Success Story Badge */}
            <div className="text-center mb-4">
              <span
                className="badge fw-semibold"
                style={{
                  background: 'linear-gradient(to right, #667eea, #764ba2)',
                  padding: '8px 16px',
                  borderRadius: '9999px',
                  fontSize: '14px',
                }}
              >
                {t('landing.benefits.successStory.badge')}
              </span>
            </div>

            {/* User Info */}
            <div className="d-flex align-items-center mb-3">
              <img
                src={assetPath('testimonials/avatar-chi-quyen.png')}
                alt={t('landing.benefits.successStory.name')}
                className="rounded-circle me-3"
                style={{ width: '64px', height: '64px', objectFit: 'cover' }}
              />
              <div>
                <h5 className="fw-bold mb-1" style={{ fontSize: '18px', color: '#111827' }}>
                  {t('landing.benefits.successStory.name')}
                </h5>
                <p className="mb-0" style={{ fontSize: '14px', color: '#6b7280' }}>
                  {t('landing.benefits.successStory.role')}
                </p>
              </div>
            </div>

            {/* Quote */}
            <p className="mb-4" style={{ fontSize: '16px', color: '#374151', lineHeight: '24px' }}>
              "{t('landing.benefits.successStory.quote')}"
            </p>

            {/* Stats */}
            <div className="row text-center">
              <div className="col-4">
                <h3 className="fw-bold mb-1" style={{ color: '#667eea', fontSize: '28px' }}>
                  {t('landing.benefits.successStory.stats.hours')}
                </h3>
                <p className="mb-0 small text-muted">
                  {t('landing.benefits.successStory.stats.hoursLabel')}
                </p>
              </div>
              <div className="col-4">
                <h3 className="fw-bold mb-1" style={{ color: '#667eea', fontSize: '28px' }}>
                  {t('landing.benefits.successStory.stats.completion')}
                </h3>
                <p className="mb-0 small text-muted">
                  {t('landing.benefits.successStory.stats.completionLabel')}
                </p>
              </div>
              <div className="col-4">
                <h3 className="fw-bold mb-1" style={{ color: '#667eea', fontSize: '28px' }}>
                  {t('landing.benefits.successStory.stats.months')}
                </h3>
                <p className="mb-0 small text-muted">
                  {t('landing.benefits.successStory.stats.monthsLabel')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Daily Feature Card Component
const DailyFeatureCard = ({ icon, title, description }) => (
  <div
    className="h-100 p-4"
    style={{
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
    }}
  >
    <div className="d-flex align-items-center mb-3">
      <i
        className={`bi ${icon} me-2`}
        style={{ fontSize: '20px', color: 'white' }}
      ></i>
      <span className="fw-bold text-white" style={{ fontSize: '18px' }}>
        {title}
      </span>
    </div>
    <p
      className="mb-0"
      style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.8)', lineHeight: '20px' }}
    >
      {description}
    </p>
  </div>
);

// Daily Feature Section Component
const DailyFeatureSection = ({ t }) => (
  <section
    className="py-5"
    style={{ background: 'linear-gradient(142.52deg, #faf5ff 0%, #eff6ff 70.71%)' }}
  >
    <div className="container">
      <div
        className="rounded-4 p-4 p-md-5 position-relative"
        style={{
          background: 'linear-gradient(to right, #667eea, #764ba2)',
          boxShadow: '0 8px 10px rgba(0,0,0,0.1), 0 20px 25px rgba(0,0,0,0.1)',
        }}
      >
        {/* Header Row */}
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center mb-4 pb-2">
          {/* Phone Icon */}
          <div
            className="d-flex align-items-center justify-content-center me-md-4 mb-3 mb-md-0 flex-shrink-0"
            style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '16px',
            }}
          >
            <i className="bi bi-phone" style={{ fontSize: '36px', color: 'white' }}></i>
          </div>

          {/* Title and Subtitle */}
          <div className="flex-grow-1">
            <h3 className="text-white fw-bold mb-2" style={{ fontSize: '30px', lineHeight: '36px' }}>
              {t('landing.dailyFeature.title')}
            </h3>
            <p
              className="mb-0"
              style={{ fontSize: '18px', color: 'rgba(255, 255, 255, 0.9)', lineHeight: '28px' }}
            >
              {t('landing.dailyFeature.subtitle')}
            </p>
          </div>

          {/* Faded 1' text */}
          <div
            className="d-none d-lg-block flex-shrink-0 ms-4"
            style={{ opacity: 0.2 }}
          >
            <span
              className="fw-bold text-white"
              style={{ fontSize: '60px', lineHeight: '72px' }}
            >
              1&apos;
            </span>
          </div>
        </div>

        {/* Feature Cards Row */}
        <div className="row g-4">
          <div className="col-md-4">
            <DailyFeatureCard
              icon="bi-check-circle-fill"
              title={t('landing.dailyFeature.viewProgress.title')}
              description={t('landing.dailyFeature.viewProgress.description')}
            />
          </div>
          <div className="col-md-4">
            <DailyFeatureCard
              icon="bi-graph-up-arrow"
              title={t('landing.dailyFeature.trackResults.title')}
              description={t('landing.dailyFeature.trackResults.description')}
            />
          </div>
          <div className="col-md-4">
            <DailyFeatureCard
              icon="bi-heart-fill"
              title={t('landing.dailyFeature.encourageTimely.title')}
              description={t('landing.dailyFeature.encourageTimely.description')}
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Why Kids Thrive Feature Card
const WhyKidsThriveCard = ({ icon, iconBg, title, description }) => (
  <div className="col-md-4">
    <div
      className="bg-white rounded-4 p-4 h-100 text-center"
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
    >
      <div
        className="rounded-3 d-inline-flex align-items-center justify-content-center mb-4"
        style={{ width: '64px', height: '64px', backgroundColor: iconBg }}
      >
        <i className={`bi ${icon}`} style={{ fontSize: '28px', color: '#667eea' }}></i>
      </div>
      <h4 className="fw-bold mb-3" style={{ fontSize: '20px', color: '#111827' }}>
        {title}
      </h4>
      <p className="mb-0" style={{ fontSize: '16px', color: '#4b5563', lineHeight: '24px' }}>
        {description}
      </p>
    </div>
  </div>
);

// Why Kids Thrive Section
const WhyKidsThriveSection = ({ t }) => (
  <section className="py-5 bg-white">
    <div className="container py-4">
      <div className="text-center mb-5">
        <h2
          className="fw-bold mb-3"
          style={{ fontSize: '36px', color: '#111827', lineHeight: '40px' }}
        >
          {t('landing.whyKidsThrive.title')}
        </h2>
        <p
          className="mx-auto"
          style={{ fontSize: '20px', color: '#4b5563', maxWidth: '768px', lineHeight: '28px' }}
        >
          {t('landing.whyKidsThrive.subtitle')}
        </p>
      </div>
      <div className="row g-4">
        <WhyKidsThriveCard
          icon="bi-controller"
          iconBg="#dbeafe"
          title={t('landing.whyKidsThrive.gameLearning.title')}
          description={t('landing.whyKidsThrive.gameLearning.description')}
        />
        <WhyKidsThriveCard
          icon="bi-robot"
          iconBg="#dcfce7"
          title={t('landing.whyKidsThrive.aiAssistant.title')}
          description={t('landing.whyKidsThrive.aiAssistant.description')}
        />
        <WhyKidsThriveCard
          icon="bi-trophy"
          iconBg="#f3e8ff"
          title={t('landing.whyKidsThrive.motivation.title')}
          description={t('landing.whyKidsThrive.motivation.description')}
        />
      </div>
    </div>
  </section>
);

// Program Card Component
const ProgramCard = ({ image, webpImage, imageAlt, title, description }) => (
  <div className="col-sm-6 col-lg-3 mb-4">
    <div
      className="bg-white rounded-3 overflow-hidden h-100"
      style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
    >
      <div
        className="overflow-hidden mx-3 mt-3"
        style={{ height: '192px', borderRadius: '8px' }}
      >
        <picture>
          <source srcSet={webpImage} type="image/webp" />
          <img
            src={image}
            alt={imageAlt}
            className="w-100 h-100"
            style={{ objectFit: 'cover' }}
          />
        </picture>
      </div>
      <div className="p-3">
        <h4 className="fw-bold mb-2" style={{ fontSize: '20px', color: '#111827' }}>
          {title}
        </h4>
        <p className="mb-0" style={{ fontSize: '14px', color: '#4b5563', lineHeight: '20px' }}>
          {description}
        </p>
      </div>
    </div>
  </div>
);

// Training Programs Section
const TrainingProgramsSection = ({ t }) => (
  <section className="py-5 bg-white" id="programs">
    <div className="container py-4">
      <div className="text-center mb-5">
        <h2
          className="fw-bold mb-3"
          style={{ fontSize: '36px', color: '#111827', lineHeight: '40px' }}
        >
          {t('landing.trainingPrograms.title')}
        </h2>
        <p style={{ fontSize: '20px', color: '#4b5563', lineHeight: '28px' }}>
          {t('landing.trainingPrograms.description')}
        </p>
      </div>

      {/* First Row - 4 programs */}
      <div className="row">
        <ProgramCard
          image={assetPath('program-english.png')}
          webpImage={assetPath('program-english.webp')}
          imageAlt={t('landing.trainingPrograms.english.imageAlt')}
          title={t('landing.trainingPrograms.english.title')}
          description={t('landing.trainingPrograms.english.description')}
        />
        <ProgramCard
          image={assetPath('program-math.png')}
          webpImage={assetPath('program-math.webp')}
          imageAlt={t('landing.trainingPrograms.math.imageAlt')}
          title={t('landing.trainingPrograms.math.title')}
          description={t('landing.trainingPrograms.math.description')}
        />
        <ProgramCard
          image={assetPath('program-coding.png')}
          webpImage={assetPath('program-coding.webp')}
          imageAlt={t('landing.trainingPrograms.coding.imageAlt')}
          title={t('landing.trainingPrograms.coding.title')}
          description={t('landing.trainingPrograms.coding.description')}
        />
        <ProgramCard
          image={assetPath('program-piano.png')}
          webpImage={assetPath('program-piano.webp')}
          imageAlt={t('landing.trainingPrograms.piano.imageAlt')}
          title={t('landing.trainingPrograms.piano.title')}
          description={t('landing.trainingPrograms.piano.description')}
        />
      </div>

      {/* Second Row - 3 programs centered */}
      <div className="row justify-content-center">
        <ProgramCard
          image={assetPath('program-chess.png')}
          webpImage={assetPath('program-chess.webp')}
          imageAlt={t('landing.trainingPrograms.chess.imageAlt')}
          title={t('landing.trainingPrograms.chess.title')}
          description={t('landing.trainingPrograms.chess.description')}
        />
        <ProgramCard
          image={assetPath('program-wisdom.png')}
          webpImage={assetPath('program-wisdom.webp')}
          imageAlt={t('landing.trainingPrograms.wisdom.imageAlt')}
          title={t('landing.trainingPrograms.wisdom.title')}
          description={t('landing.trainingPrograms.wisdom.description')}
        />
        <ProgramCard
          image={assetPath('program-physical.png')}
          webpImage={assetPath('program-physical.webp')}
          imageAlt={t('landing.trainingPrograms.physical.imageAlt')}
          title={t('landing.trainingPrograms.physical.title')}
          description={t('landing.trainingPrograms.physical.description')}
        />
      </div>
    </div>
  </section>
);

// Testimonial Card Component
const TestimonialCard = ({ initials, gradientStyle, avatar, name, role, quote }) => (
  <div className="col-md-4">
    <div
      className="rounded-4 p-4 h-100"
      style={{ backgroundColor: '#f9fafb' }}
    >
      <div className="d-flex align-items-center mb-3">
        {avatar ? (
          <img
            src={avatar}
            alt={name}
            className="rounded-circle me-3"
            style={{ width: '48px', height: '48px', objectFit: 'cover' }}
          />
        ) : (
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-3 fw-bold text-white"
            style={{ width: '48px', height: '48px', background: gradientStyle }}
          >
            {initials}
          </div>
        )}
        <div>
          <h5 className="fw-semibold mb-0" style={{ fontSize: '16px', color: '#111827' }}>
            {name}
          </h5>
          <p className="mb-0" style={{ fontSize: '14px', color: '#4b5563' }}>
            {role}
          </p>
        </div>
      </div>
      <p className="mb-0" style={{ fontSize: '16px', color: '#374151', lineHeight: '24px' }}>
        "{quote}"
      </p>
    </div>
  </div>
);

// Testimonials Section
const TestimonialsSection = ({ t }) => (
  <section className="py-5 bg-white">
    <div className="container py-4">
      <div className="text-center mb-5">
        <h2
          className="fw-bold mb-3"
          style={{ fontSize: '36px', color: '#111827', lineHeight: '40px' }}
        >
          {t('landing.testimonials.title')}
        </h2>
        <p style={{ fontSize: '20px', color: '#4b5563', lineHeight: '28px' }}>
          {t('landing.testimonials.description')}
        </p>
      </div>
      <div className="row g-4">
        <TestimonialCard
          avatar={assetPath('testimonials/avatar-chi-quyen.png')}
          name={t('landing.testimonials.testimonial1.name')}
          role={t('landing.testimonials.testimonial1.role')}
          quote={t('landing.testimonials.testimonial1.quote')}
        />
        <TestimonialCard
          avatar={assetPath('testimonials/avatar-anh-hiep.png')}
          name={t('landing.testimonials.testimonial2.name')}
          role={t('landing.testimonials.testimonial2.role')}
          quote={t('landing.testimonials.testimonial2.quote')}
        />
        <TestimonialCard
          avatar={assetPath('testimonials/avatar-chi-van.png')}
          name={t('landing.testimonials.testimonial3.name')}
          role={t('landing.testimonials.testimonial3.role')}
          quote={t('landing.testimonials.testimonial3.quote')}
        />
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = ({ loginPath, t }) => (
  <section
    className="py-5"
    style={{
      background: 'linear-gradient(16.88deg, #667eea 50%, #764ba2 120.71%)',
    }}
  >
    <div className="container py-5">
      <div className="text-center text-white">
        <h2 className="fw-bold mb-4" style={{ fontSize: '48px', lineHeight: '48px' }}>
          {t('landing.cta.title')}
        </h2>
        <p
          className="mb-5 mx-auto"
          style={{ fontSize: '20px', lineHeight: '33px', maxWidth: '848px', color: '#f3f4f6' }}
        >
          {t('landing.cta.description')}
        </p>
        <a
          href={loginPath}
          className="btn btn-light btn-lg fw-semibold px-4 d-inline-flex align-items-center gap-2"
          style={{ borderRadius: '8px', color: '#667eea' }}
        >
          {t('landing.cta.startNow')}
          <span
            className="badge text-white fw-semibold"
            style={{
              backgroundColor: '#22c55e',
              borderRadius: '9999px',
              fontSize: '12px',
              padding: '4px 10px',
            }}
          >
            {t('landing.cta.freeBadge')}
          </span>
        </a>
      </div>
    </div>
  </section>
);

// Footer Section
const FooterSection = ({ currentYear, t, socialLinks = {}, livechatEnabled = false }) => (
  <footer className="py-5" style={{ backgroundColor: '#111827' }}>
    <div className="container">
      <div className="row">
        {/* Brand Column */}
        <div className="col-lg-5 mb-4 mb-lg-0">
          <div className="d-flex align-items-center mb-3">
            <img
              src={assetPath('powerkid-icon.png')}
              alt="PowerKid Logo"
              className="me-2"
              style={{ width: '40px', height: '40px' }}
            />
            <span className="fw-bold text-white" style={{ fontSize: '24px' }}>
              PowerKid
            </span>
          </div>
          <p className="mb-4" style={{ color: '#9ca3af', maxWidth: '448px', lineHeight: '24px' }}>
            {t('landing.footer.brandDescription')}
          </p>
          {(socialLinks.facebook || socialLinks.youtube || socialLinks.tiktok) && (
            <div className="d-flex gap-3">
              {socialLinks.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af' }} aria-label="Facebook">
                  <i className="bi bi-facebook" style={{ fontSize: '20px' }}></i>
                </a>
              )}
              {socialLinks.youtube && (
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af' }} aria-label="YouTube">
                  <i className="bi bi-youtube" style={{ fontSize: '20px' }}></i>
                </a>
              )}
              {socialLinks.tiktok && (
                <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer" style={{ color: '#9ca3af' }} aria-label="TikTok">
                  <i className="bi bi-tiktok" style={{ fontSize: '20px' }}></i>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Product Links */}
        <div className="col-sm-6 col-lg-3 mb-4 mb-lg-0">
          <h5 className="fw-semibold text-white mb-3">{t('landing.footer.product.title')}</h5>
          <ul className="list-unstyled">
            <li className="mb-2">
              <a href="/programs" className="text-decoration-none" style={{ color: '#9ca3af' }}>
                {t('landing.footer.product.programs')}
              </a>
            </li>
            <li className="mb-2">
              <a href="/features" className="text-decoration-none" style={{ color: '#9ca3af' }}>
                {t('landing.footer.product.features')}
              </a>
            </li>
            <li className="mb-2">
              <a href="/pricing" className="text-decoration-none" style={{ color: '#9ca3af' }}>
                {t('landing.footer.product.pricing')}
              </a>
            </li>
          </ul>
        </div>

        {/* Support Links */}
        <div className="col-sm-6 col-lg-3">
          <h5 className="fw-semibold text-white mb-3">{t('landing.footer.support.title')}</h5>
          <ul className="list-unstyled">
            {livechatEnabled && (
              <li className="mb-2">
                <a
                  href="#"
                  className="text-decoration-none"
                  style={{ color: '#9ca3af', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (window.LiveChatWidget) {
                      window.LiveChatWidget.call('maximize');
                    }
                  }}
                >
                  {t('landing.footer.support.contactUs')}
                </a>
              </li>
            )}
            <li className="mb-2">
              <a href="/privacy" className="text-decoration-none" style={{ color: '#9ca3af' }}>
                {t('landing.footer.support.privacyPolicy')}
              </a>
            </li>
            <li className="mb-2">
              <a href="/tos" className="text-decoration-none" style={{ color: '#9ca3af' }}>
                {t('landing.footer.support.termsOfService')}
              </a>
            </li>
            <li className="mb-2">
              <a href="/faq" className="text-decoration-none" style={{ color: '#9ca3af' }}>
                {t('landing.footer.support.faq')}
              </a>
            </li>
          </ul>
        </div>
      </div>

      <hr className="my-4" style={{ borderColor: '#374151' }} />

      <div className="text-center" style={{ color: '#9ca3af' }}>
        <p className="mb-2">
          &copy; {currentYear} {t('landing.footer.copyright')}
        </p>
        <p className="mb-1" style={{ fontSize: '14px' }}>
          {t('landing.footer.company.name')}
        </p>
        <p className="mb-0" style={{ fontSize: '14px' }}>
          {t('landing.footer.company.address')}
        </p>
      </div>
    </div>
  </footer>
);

// Flash Message Component
const FlashMessage = ({ notice, alert }) => {
  if (!notice && !alert) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '90px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1050,
        maxWidth: '600px',
        width: '90%',
      }}
    >
      {notice && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {notice}
          <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      )}
      {alert && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {alert}
          <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        </div>
      )}
    </div>
  );
};

// Main Landing Page Component
const LandingPage = ({ locale = 'vi', loginPath = '/login', flashNotice, flashAlert }) => {
  // Initialize i18n only once on mount
  const i18nInitialized = useRef(false);
  if (!i18nInitialized.current) {
    initI18n(locale);
    i18nInitialized.current = true;
  }
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  // Fetch config from API
  const [socialLinks, setSocialLinks] = useState({});
  const [livechatEnabled, setLivechatEnabled] = useState(false);
  useEffect(() => {
    fetch('/api/frontend_config')
      .then((res) => res.json())
      .then((data) => {
        if (data.social_links) {
          setSocialLinks(data.social_links);
        }
        if (data.livechat_enabled !== undefined) {
          setLivechatEnabled(data.livechat_enabled);
        }
      })
      .catch(() => {
        // Silently fail - social links just won't show, livechat defaults to true
      });
  }, []);

  return (
    <div className="d-flex flex-column min-vh-100">
      <Header loginPath={loginPath} t={t} />
      <FlashMessage notice={flashNotice} alert={flashAlert} />
      <main className="flex-fill">
        <HeroSection loginPath={loginPath} t={t} />
        <BenefitsSection t={t} />
        <DailyFeatureSection t={t} />
        <WhyKidsThriveSection t={t} />
        <TrainingProgramsSection t={t} />
        <TestimonialsSection t={t} />
        <CTASection loginPath={loginPath} t={t} />
      </main>
      <FooterSection currentYear={currentYear} t={t} socialLinks={socialLinks} livechatEnabled={livechatEnabled} />
    </div>
  );
};

export default LandingPage;
